import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonContent, IonModal } from "@ionic/react";
import { memo, ReactNode, useRef } from "react";
import { StackedModalHeader } from "./stacked-modal-header";

interface StackedModalProps {
  pageRef?: React.RefObject<HTMLElement>;
  isOpen: boolean;
  onDidDismiss: () => void;
  onDidPresent?: () => void;
  backdropDismiss?: boolean;
  children?: ReactNode;
  classNames?: {
    modal?: string;
    header?: string;
    content?: string;
  };
  title?: string;
  subtitle?: string;
  titleFontSize?: keyof typeof textVariants;
  subtitleFontSize?: keyof typeof textVariants;
  showHeader?: boolean;
  initialBreakpoint?: number;
  breakpoints?: number[];
  onConfirm?: () => void;
  showConfirmButton?: boolean;
  disabledConfirmButton?: boolean;
  headerBottomContent?: ReactNode;
  renderCloseButton?: (onClose: () => void) => ReactNode;
  forceOverscroll?: boolean;
  /** When false, IonContent does not scroll — children manage their own scroll areas */
  scrollY?: boolean;
}

export { StackedModalHeader } from "./stacked-modal-header";

export const StackedModal = memo(function StackedModal({
  isOpen,
  onDidDismiss,
  onDidPresent,
  backdropDismiss = true,
  children,
  classNames,
  title,
  subtitle,
  titleFontSize,
  subtitleFontSize,
  showHeader = true,
  onConfirm,
  showConfirmButton = true,
  disabledConfirmButton = false,
  headerBottomContent,
  initialBreakpoint,
  breakpoints,
  renderCloseButton,
  forceOverscroll,
  scrollY = true,
}: StackedModalProps) {
  const modalRef = useRef<HTMLIonModalElement>(null);

  // Only present as a draggable sheet when real breakpoints are supplied;
  // otherwise this is a plain full-screen modal. We intentionally do NOT pass
  // `presentingElement`: in the app's forced iOS mode that triggers the
  // stacked-card effect (scales the page to 0.915 and paints the body black),
  // which read as an unwanted black frame around every modal. Matches AiChatModal.
  const breakpointProps = breakpoints?.length
    ? { breakpoints, initialBreakpoint: initialBreakpoint ?? breakpoints[breakpoints.length - 1] }
    : {};

  const handleClose = () => {
    if (modalRef.current) {
      modalRef.current.dismiss();
      return;
    }
    onDidDismiss();
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      onDidPresent={onDidPresent}
      backdropDismiss={backdropDismiss}
      className={classNames?.modal}
      {...breakpointProps}
    >
      {showHeader && (
        <StackedModalHeader
          title={title}
          subtitle={subtitle}
          titleFontSize={titleFontSize}
          subtitleFontSize={subtitleFontSize}
          onClose={handleClose}
          onConfirm={onConfirm}
          disabledConfirmButton={disabledConfirmButton}
          showConfirmButton={showConfirmButton}
          headerBottomContent={headerBottomContent}
          renderCloseButton={renderCloseButton}
          className={classNames?.header}
        />
      )}

      <IonContent
        className={cn("ion-padding", classNames?.content)}
        forceOverscroll={forceOverscroll}
        scrollY={scrollY}
      >
        {children}
      </IonContent>
    </IonModal>
  );
});
